export function pairs<T>(items: T[]): [T, T][] {
  if (items.length < 2) return [];

  const acc: [T, T][] = [];
  const seed = {
    acc,
    prev: items[0],
  };

  return items.slice(1).reduce(({ acc, prev }, x) => {
    const pair: [T, T] = [prev, x];
    return { acc: [...acc, pair], prev: x };
  }, seed).acc;
}

export function forceUnique(integers: number[]) {
  const set = new Set(integers);
  if (set.size === integers.length) return integers;

  const ordered = Array.from(set.values()).sort((a, b) => a - b);

  const gaps = pairs(ordered)
    .filter(([a, b]) => b - a > 1)
    .map(([a, b]) => new Array(b - a - 1).fill(0).map((_, i) => a + 1 + i))
    .flat();

  const filledGaps = gaps
    .slice(0, integers.length - set.size)
    .reduce((set, x) => set.add(x), set);

  const needed = integers.length - filledGaps.size;

  const afterAdded = new Array<number>(needed).fill(0).reduce((set) => {
    const min = Math.min.apply(0, Array.from(set.values()));
    const max = Math.max.apply(0, Array.from(set.values()));

    const side = Math.abs(max) <= Math.abs(min) ? 'end' : 'start';

    const next = side === 'end' ? max + 1 : min - 1;

    return set.add(next);
  }, filledGaps);

  return Array.from(afterAdded.values()).sort((a, b) => a - b);
}

export function reorderPriorities(
  priorities: { [id: string]: number },
  desiredOrder: string[]
): { [id: string]: number } {
  if (Object.keys(priorities).length < 2) return priorities;

  const ordered = forceUnique(Object.values(priorities).sort((a, b) => a - b));

  const assigned = ordered
    .map((priority, index) => ({ id: desiredOrder[index], priority }))
    .reduce(
      (acc, { id, priority }) => ({ ...acc, [id]: priority }),
      {} as { [id: string]: number }
    );

  return assigned;
}
